/**
 * Untouched heights of the ridden chunks, regenerated off the main thread (the chunk worker runs the same
 * generateChunk), so a how-to demo can put the ground back without a 1-2 s stall. Warmed when a failure is offered;
 * a chunk not ready yet falls back to generating inline (pristine.ts), so the demo is exact either way.
 */
import type { ChunkedTerrain } from '../sim/chunks';
import { CHUNK_SIZE } from '../sim/grid';
import type { LevelId } from '../sim/levels';

const key = (ci: number, cj: number) => `${ci},${cj}`;

export class PristineCache {
  private readonly heights = new Map<string, Float32Array>();
  private readonly pending = new Set<string>();
  private worker: Worker | null = null;

  constructor(private readonly levelId: LevelId) {}

  get = (ci: number, cj: number): Float32Array | undefined => this.heights.get(key(ci, cj));

  /**
   * Requests every loaded chunk that carries ruts, and the one under the rider (it is about to get some: asking
   * early also gets the worker's level load out of the way), unless cached or asked already.
   */
  warm(terrain: ChunkedTerrain, x: number, z: number): void {
    const here = key(Math.floor(x / CHUNK_SIZE), Math.floor(z / CHUNK_SIZE));
    // Only loaded chunks are ever asked for: a cached chunk the rider has left is 4 MB nobody needs (it is requested again).
    const loaded = new Set(terrain.loaded().map((c) => key(c.ci, c.cj)));
    for (const k of this.heights.keys()) if (!loaded.has(k)) this.heights.delete(k);
    for (const c of terrain.loaded()) {
      const k = key(c.ci, c.cj);
      if ((!c.rut && k !== here) || this.heights.has(k) || this.pending.has(k)) continue;
      this.pending.add(k);
      this.ensureWorker().postMessage({ levelId: this.levelId, ci: c.ci, cj: c.cj });
    }
  }

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    const w = new Worker(new URL('../sim/chunkWorker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (e: MessageEvent<{ ci: number; cj: number; field: { heights: Float32Array } }>) => {
      const k = key(e.data.ci, e.data.cj);
      this.pending.delete(k);
      this.heights.set(k, e.data.field.heights);
    };
    return (this.worker = w);
  }
}
